import { useEffect, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { FileSpreadsheet, CheckCircle2, Loader, Trash } from "lucide-react";
import * as XLSX from "xlsx";
import { Button } from "@ui/Button";
import { getFileDisplayName, handleFileDownload } from "@/utils/download";
import { cn } from "@/utils/tailwind";

interface UploadedFileProps {
  file?: File | string | null;
  uploading: boolean;
  onRemove: () => void;
  isError?: boolean;
  dataTest?: string;
}

async function getExcelFileInfo(file: File) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer);
  const sheet = workbook.Sheets[workbook.SheetNames[0]];

  const allRows = XLSX.utils.sheet_to_json<[number, number]>(sheet, { header: 1 });

  console.log("All rows:", allRows);

  const maxInSecondCol = Math.max(
    ...allRows.map(row => {
      const val = row[1];
      return typeof val === "number" ? val : -Infinity;
    }),
  );

  return { maxInSecondCol };
}

const UploadedFile: FC<UploadedFileProps> = ({ file, uploading, onRemove, isError, dataTest }) => {
  const { t } = useTranslation();
  const isDone = !uploading;
  const isFile = file instanceof File;
  const fileName = getFileDisplayName(file);

  const [maxPowerDemand, setMaxPowerDemand] = useState<number>(-Infinity);

  useEffect(() => {
    if (isFile) {
      getExcelFileInfo(file)
        .then(({ maxInSecondCol }) => {
          setMaxPowerDemand(maxInSecondCol);
        })
        .catch((error: unknown) => {
          console.error("Error reading Excel file:", error);
        });
    }
  }, [file, isFile]);

  return (
    <div
      className={cn(
        "border-input bg-background flex h-[97px] w-full items-center justify-between rounded border px-4 md:px-8",
        isError && "border-destructive",
      )}
      data-test={dataTest ? `${dataTest}-file` : undefined}
    >
      <div className="flex w-full items-center gap-4">
        <FileSpreadsheet className="size-8 shrink-0 md:size-[49px]" strokeWidth={1} />
        <div className="flex flex-1 flex-col md:gap-2">
          <Button
            variant="link"
            onClick={() => handleFileDownload(fileName, t, file)}
            className="text-foreground line-clamp-1 h-auto p-0 text-left text-base font-medium break-all"
            data-test={dataTest ? `${dataTest}-file-name` : undefined}
          >
            {fileName}
          </Button>
          <div className="flex items-center gap-2 text-xs font-extralight">
            {isFile && (
              <>
                <span>
                  {t("mbOfMbMax", { mb: (file.size / (1024 * 1024)).toFixed(2), mb_max: 10 })}
                </span>
                <span>•</span>
                <span>
                  {t("powerDemand", {
                    value: isFinite(maxPowerDemand) ? maxPowerDemand.toFixed(2) : "---",
                  })}
                </span>
                <span>•</span>
              </>
            )}
            {uploading && isFile && (
              <span className="flex items-center gap-1">
                <Loader className="size-4 animate-spin" />
                {t("loading")}
              </span>
            )}
            {isDone && (
              <span
                className="flex items-center gap-1"
                data-test={dataTest ? `${dataTest}-upload-complete` : undefined}
              >
                <CheckCircle2 className="text-background size-4 fill-[#4DA84A]" />
                {t("done")}
              </span>
            )}
          </div>
        </div>
      </div>

      <Button
        onClick={onRemove}
        variant="ghost"
        className="p-0"
        data-test={dataTest ? `${dataTest}-remove-button` : undefined}
      >
        <Trash className="size-6" strokeWidth={1.5} />
      </Button>
    </div>
  );
};

export default UploadedFile;
